/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

/**
 * The Stream: one instance of a process the console can watch and act on, whoever holds the pen.
 * Scheduling is the first producer, so today a Stream is composed from a single agent run; this
 * module is the generalization seam. When nexus ships `/v1/streams`, only the composition below
 * changes — the shape the UI consumes does not.
 *
 * A scheduling stream reads as a conversation: the agent reaches out to families, they reply by
 * text, and when the agent isn't sure it turns to a human. So we project the run into messages,
 * not a timeline — the human is simply the next voice in the thread.
 *
 * "Stream" is the internal contract noun only; it never appears in the UI.
 */

import type { OutreachStep, PayerRef, RunDetail, RunExclusion, RunProvider } from "../api/runs";

export type StreamState = "WORKING" | "AWAITING_FAMILY" | "AWAITING_HUMAN" | "DONE";

export type Controller = "AGENT" | "HUMAN" | "NONE";

export type MessageAuthor = "AGENT" | "FAMILY" | "SYSTEM";

export type MessageTone = "default" | "attention" | "positive";

/** One turn in the conversation. The agent narrates, families reply by text, the system marks events. */
export type StreamMessage = {
  id: string;
  author: MessageAuthor;
  name: string;
  at: string | null;
  text: string;
  tone: MessageTone;
  /** Secondary lines under the message: a ranked shortlist, exclusion reasons. */
  detail: string[];
  /** Present only on the "ranked the waitlist" step: the structured shortlist behind the summary. */
  ranking?: Ranking;
  /** Set when the run's pool had nobody: the provider's states, for a current-waitlist link. */
  poolStates?: string[];
  viaText: boolean;
  needsYou: boolean;
};

/** The waitlist decision, structured for the ranking modal: who was contacted, who was skipped and why. */
export type Ranking = {
  shortlist: {
    rank: number;
    name: string;
    familyId: number | null;
    score: number;
    acuity: string | null;
    fromTransition: boolean;
  }[];
  skipped: SkippedCandidate[];
};

/** One candidate the agent passed over, named, with the insurance context when it applies. */
export type SkippedCandidate = {
  name: string;
  familyId: number | null;
  detail: string;
  payer: PayerRef | null;
  /** What the provider accepts in the candidate's state; null when not an insurance skip. */
  providerAccepts: PayerRef[] | null;
};

/** A point where a human is needed: an open coordinator escalation the human can claim and resolve. */
export type Handoff = {
  entryId: string;
  reasoning: string;
  confidence: number | null;
  /** Per-component confidence scores (0..1) the gate weighed before turning to a human. */
  confidenceBreakdown: Record<string, number> | null;
  claimed: boolean;
  claimedByUserId: string | null;
};

export type Stream = {
  id: string;
  kind: "scheduling-offer";
  trigger: string;
  status: string;
  subjectName: string;
  provider: RunProvider | null;
  slotStartsAt: string | null;
  slotEndsAt: string | null;
  patientId: string | null;
  patientExternalId: string | null;
  state: StreamState;
  controller: Controller;
  attention: boolean;
  startedAt: string;
  endedAt: string | null;
  reasoning: string | null;
  handoffs: Handoff[];
  messages: StreamMessage[];
};

const TERMINAL_RUN_STATUSES = new Set([
  "COMPLETED_BOOKED",
  "COMPLETED_FALLBACK",
  "FAILED",
]);

const AWAITING_REPLY_OUTREACH = new Set(["SENT", "REMINDED"]);

function humanize(value: string): string {
  return value.replaceAll("_", " ").toLowerCase();
}

function candidateName(step: OutreachStep): string {
  const name = [step.patientFirstName, step.patientLastName]
    .filter((part) => part !== null)
    .join(" ");
  return name === "" ? "a family" : name;
}

function byRank(outreach: OutreachStep[]): OutreachStep[] {
  return [...outreach].sort((a, b) => a.outreach.rankPosition - b.outreach.rankPosition);
}

function openEscalation(detail: RunDetail) {
  return detail.escalations.find((e) => e.status === "OPEN" || e.status === "CLAIMED");
}

function escalatedStep(detail: RunDetail): OutreachStep | undefined {
  const open = openEscalation(detail);
  if (open === undefined) {
    return undefined;
  }
  return detail.outreach.find((step) => step.outreach.id === open.agentOutreachId);
}

function subjectOf(detail: RunDetail): string {
  const escalated = escalatedStep(detail);
  if (escalated !== undefined) {
    return candidateName(escalated);
  }
  const booked = detail.outreach.find((step) => step.outreach.booked);
  if (booked !== undefined) {
    return candidateName(booked);
  }
  const responded = detail.outreach.find((step) => step.outreach.response !== null);
  if (responded !== undefined) {
    return candidateName(responded);
  }
  const top = byRank(detail.outreach)[0];
  if (top !== undefined) {
    return candidateName(top);
  }
  return providerFallbackName(detail) ?? "a family";
}

function openHandoffs(detail: RunDetail): Handoff[] {
  return detail.escalations
    .filter((entry) => entry.status === "OPEN" || entry.status === "CLAIMED")
    .map((entry) => ({
      entryId: entry.id,
      reasoning: entry.reasoning,
      confidence: entry.confidence,
      confidenceBreakdown: entry.confidenceBreakdown,
      claimed: entry.status === "CLAIMED",
      claimedByUserId: entry.claimedByUserId,
    }));
}

function deriveState(detail: RunDetail, handoffs: Handoff[]): StreamState {
  if (handoffs.length > 0) {
    return "AWAITING_HUMAN";
  }
  if (TERMINAL_RUN_STATUSES.has(detail.run.status)) {
    return "DONE";
  }
  const awaitingFamily = detail.outreach.some((step) =>
    AWAITING_REPLY_OUTREACH.has(step.outreach.status),
  );
  return awaitingFamily ? "AWAITING_FAMILY" : "WORKING";
}

function deriveController(state: StreamState): Controller {
  switch (state) {
    case "AWAITING_HUMAN":
      return "HUMAN";
    case "DONE":
      return "NONE";
    default:
      return "AGENT";
  }
}

function familyReplyText(step: OutreachStep): string | null {
  if (step.responseBody !== null && step.responseBody.trim() !== "") {
    return step.responseBody;
  }
  if (step.outreach.response === "YES") {
    return "Yes";
  }
  if (step.outreach.response === "NO") {
    return "No";
  }
  return null;
}

/** Only the shortlist the agent actually ranked, whatever the summary shape. */
function shortlistSize(summary: RunDetail["run"]["resultSummary"]): number {
  return (summary?.shortlist ?? []).length;
}

/**
 * The run's headline, stated as what happened rather than what the agent attempts. An empty
 * shortlist means nobody matched; a shortlist with nothing sent means everyone was ranked but
 * none could be texted (no SMS opt-in, rate limit, opted out). Only a real send count may
 * claim the agent reached anyone.
 */
function decidedText(shortlisted: number, texted: number): string {
  if (shortlisted === 0) {
    return "No families on the waitlist matched this slot.";
  }
  if (texted === 0) {
    return "Ranked the waitlist, but no family could be contacted for this slot.";
  }
  return `Ranked the waitlist and reached out to ${texted} ${
    texted === 1 ? "family" : "families"
  }, best match first.`;
}

function decidedMessage(detail: RunDetail): StreamMessage | null {
  const ranked = byRank(detail.outreach);
  const summary = detail.run.resultSummary;
  if (ranked.length === 0 && summary === null) {
    return null;
  }
  const names = new Map(
    detail.outreach.map((step) => [step.outreach.patientId, candidateName(step)]),
  );
  const texted = ranked.filter((step) => step.outreach.smsSentAt !== null).length;
  const text = decidedText(shortlistSize(summary), texted);
  const familyIds = new Map(
    detail.outreach.map((step) => [step.outreach.patientId, step.patientFamilyId]),
  );
  const shortlist = (summary?.shortlist ?? []).map((entry) => ({
    rank: entry.rank,
    name: names.get(entry.patientId) ?? "A family",
    familyId: familyIds.get(entry.patientId) ?? null,
    score: entry.score,
    acuity: entry.acuity === undefined ? null : entry.acuity.toLowerCase(),
    fromTransition: entry.source === "TRANSITION",
  }));
  const skipped = skippedCandidates(detail);
  const firstSent = ranked.map((step) => step.outreach.smsSentAt).find((at) => at !== null) ?? null;
  return {
    id: `${detail.run.id}:decided`,
    author: "AGENT",
    name: "Scheduling agent",
    at: firstSent,
    text,
    tone: "default",
    detail: [],
    ranking: { shortlist, skipped },
    viaText: false,
    needsYou: false,
  };
}

function listStates(states: string[]): string {
  if (states.length <= 1) {
    return states[0] ?? "";
  }
  return `${states.slice(0, -1).join(", ")} or ${states[states.length - 1]}`;
}

function skippedName(entry: RunExclusion): string {
  const name = [entry.patientFirstName, entry.patientLastName]
    .filter((part) => part !== null)
    .join(" ");
  return name === "" ? "A family" : name;
}

function skippedCandidates(detail: RunDetail): SkippedCandidate[] {
  if (detail.exclusions !== undefined && detail.exclusions.length > 0) {
    return detail.exclusions.map((entry) => ({
      name: skippedName(entry),
      familyId: entry.familyId,
      detail: entry.detail ?? humanize(entry.reason ?? "excluded"),
      payer: entry.payer,
      providerAccepts: entry.providerAccepts,
    }));
  }
  return (detail.run.resultSummary?.exclusions ?? []).map((entry) => ({
    name: "A family",
    familyId: null,
    detail: entry.detail ?? humanize(entry.reason),
    payer: null,
    providerAccepts: null,
  }));
}

function providerFallbackName(detail: RunDetail): string | null {
  const provider = detail.provider;
  if (provider === null || provider === undefined) {
    return null;
  }
  const name = [provider.firstName, provider.lastName]
    .filter((part) => part !== null)
    .join(" ");
  return name === "" ? null : `${name}'s freed slot`;
}

function fateText(step: OutreachStep, name: string): string | null {
  const { status, outcome, skipReason } = step.outreach;
  if (status === "TIMEOUT") {
    return `No reply from ${name} in time. The agent moved on to the next family.`;
  }
  if (outcome === "SLOT_FILLED") {
    return `The slot was taken before ${name} could be booked, so the agent stood down.`;
  }
  if (outcome === "OPTED_OUT") {
    return `${name} asked to stop text messages. The agent will not text this number again.`;
  }
  if (status === "SKIPPED") {
    return `Held off on ${name}: ${skipReason ?? humanize(outcome ?? "not eligible right now")}.`;
  }
  return null;
}

function offerMessages(detail: RunDetail): StreamMessage[] {
  const messages: StreamMessage[] = [];
  for (const step of byRank(detail.outreach)) {
    const name = candidateName(step);
    if (step.outreach.smsSentAt === null) {
      const fate = fateText(step, name);
      if (fate !== null) {
        messages.push({
          id: `fate:${step.outreach.id}`,
          author: "SYSTEM",
          name: "",
          at: null,
          text: fate,
          tone: "default",
          detail: [],
          viaText: false,
          needsYou: false,
        });
      }
      continue;
    }
    messages.push({
      id: `offer:${step.outreach.id}`,
      author: "AGENT",
      name: "Scheduling agent",
      at: step.outreach.smsSentAt,
      text: `Reached out to ${name}.`,
      tone: "default",
      detail: [],
      viaText: false,
      needsYou: false,
    });
    const reply = familyReplyText(step);
    if (reply !== null) {
      messages.push({
        id: `reply:${step.outreach.id}`,
        author: "FAMILY",
        name,
        at: step.outreach.responseAt,
        text: reply,
        tone: "default",
        detail: [],
        viaText: true,
        needsYou: false,
      });
    }
    if (step.outreach.booked && step.outreach.bookedHealthieAppointmentId !== null) {
      messages.push({
        id: `booked:${step.outreach.id}`,
        author: "AGENT",
        name: "Scheduling agent",
        at: step.outreach.responseAt,
        text: `Booked ${candidateName(step)} into the open slot.`,
        tone: "positive",
        detail: [`Appointment ${step.outreach.bookedHealthieAppointmentId}`],
        viaText: false,
        needsYou: false,
      });
    } else {
      const fate = fateText(step, name);
      if (fate !== null) {
        messages.push({
          id: `fate:${step.outreach.id}`,
          author: "SYSTEM",
          name: "",
          at: null,
          text: fate,
          tone: "default",
          detail: [],
          viaText: false,
          needsYou: false,
        });
      }
    }
  }
  return messages;
}

function escalationMessages(detail: RunDetail): StreamMessage[] {
  return detail.escalations.map((entry) => {
    if (entry.status === "RESOLVED") {
      const note = entry.resolutionNote === null ? "" : ` ${entry.resolutionNote}`;
      return {
        id: `escalation:${entry.id}`,
        author: "SYSTEM",
        name: "",
        at: entry.resolvedAt,
        text: `A coordinator resolved this.${note}`,
        tone: "default",
        detail: [],
        viaText: false,
        needsYou: false,
      };
    }
    const noCandidates = shortlistSize(detail.run.resultSummary) === 0;
    const skipped = noCandidates ? skippedCandidates(detail) : [];
    const states = detail.provider?.licensedStates ?? [];
    const emptyPool = noCandidates && skipped.length === 0 && entry.patientId === null;
    return {
      id: `escalation:${entry.id}`,
      author: "AGENT",
      name: "Scheduling agent",
      at: entry.createdAt,
      text: entry.reasoning,
      tone: "attention",
      detail:
        emptyPool && states.length > 0
          ? [`Nobody was on the waitlist for ${listStates(states)} when this slot freed.`]
          : [],
      ...(skipped.length > 0 ? { ranking: { shortlist: [], skipped } } : {}),
      ...(emptyPool && states.length > 0 ? { poolStates: states } : {}),
      viaText: false,
      needsYou: true,
    };
  });
}

export function buildConversation(detail: RunDetail): StreamMessage[] {
  const decided = decidedMessage(detail);
  const messages: StreamMessage[] = [];
  const openNoCandidates =
    shortlistSize(detail.run.resultSummary) === 0 &&
    detail.escalations.some((entry) => entry.status !== "RESOLVED");
  if (decided !== null && !openNoCandidates) {
    messages.push(decided);
  }
  messages.push(...offerMessages(detail));
  messages.push(...escalationMessages(detail));
  return messages;
}

export function streamFromRunDetail(detail: RunDetail): Stream {
  const handoffs = openHandoffs(detail);
  const state = deriveState(detail, handoffs);
  const headlineEscalation = detail.escalations.find((entry) => entry.status !== "RESOLVED");
  const escalated = escalatedStep(detail);
  return {
    id: detail.run.id,
    kind: "scheduling-offer",
    trigger: detail.run.trigger,
    status: detail.run.status,
    subjectName: subjectOf(detail),
    provider: detail.provider ?? null,
    slotStartsAt: detail.run.freedSlotStartsAt,
    slotEndsAt: detail.run.freedSlotEndsAt,
    patientId: escalated?.outreach.patientId ?? openEscalation(detail)?.patientId ?? null,
    patientExternalId: escalated?.patientExternalId ?? null,
    state,
    controller: deriveController(state),
    attention: handoffs.length > 0,
    startedAt: detail.run.startedAt,
    endedAt: detail.run.completedAt,
    reasoning: headlineEscalation?.reasoning ?? detail.run.resultSummary?.outcome ?? null,
    handoffs,
    messages: buildConversation(detail),
  };
}

export function stateLabel(state: StreamState): string {
  switch (state) {
    case "WORKING":
      return "Agent working";
    case "AWAITING_FAMILY":
      return "Waiting on the family";
    case "AWAITING_HUMAN":
      return "Needs your attention";
    case "DONE":
      return "Done";
  }
}
